// Thunks for the flag-gated New Season editors. They only write the
// new_season_data slice; V1 state (distribution_data) is untouched.
import {
  get_v2_distributions,
  get_season,
  get_farming_unit_tree,
  put_farming_unit_layout,
  put_season,
  v2_error_message,
} from '../../managers/newSeasonManager';

export const newSeasonTreeKey = (distribution_id, farming_unit_id) =>
  distribution_id + '/' + farming_unit_id;

const tree_success = (tree_key, tree) => ({
  type: 'NEW_SEASON_TREE_SUCCESS',
  tree_key,
  tree,
});

export var load_new_season_tree = (distribution_id, farming_unit_id) => {
  return dispatch => {
    const tree_key = newSeasonTreeKey(distribution_id, farming_unit_id);
    dispatch({ type: 'NEW_SEASON_TREE_INIT', tree_key });
    return get_farming_unit_tree(distribution_id, farming_unit_id).then(
      tree => {
        dispatch(tree_success(tree_key, tree));
        return tree;
      },
      err => {
        dispatch({
          type: 'NEW_SEASON_TREE_FAIL',
          tree_key,
          error: v2_error_message(err),
        });
      }
    );
  };
};

// The layout PUT returns only {saved, farming_unit_id}; on success the tree is
// re-fetched with GET .../tree. Resolves with the new tree; rejects with a
// user-facing message (a failed re-fetch after a successful save rejects too,
// with saved: true, so the UI can tell the user to reload).
export var save_new_season_layout = (
  distribution_id,
  farming_unit_id,
  body
) => {
  return dispatch => {
    const tree_key = newSeasonTreeKey(distribution_id, farming_unit_id);
    return put_farming_unit_layout(distribution_id, farming_unit_id, body).then(
      () =>
        get_farming_unit_tree(distribution_id, farming_unit_id).then(
          tree => {
            dispatch(tree_success(tree_key, tree));
            return tree;
          },
          err => Promise.reject({ saved: true, message: v2_error_message(err) })
        ),
      err => Promise.reject({ saved: false, message: v2_error_message(err) })
    );
  };
};

// Resolves with the saved season; rejects with a user-facing message.
export var save_new_season_season = (
  distribution_id,
  farming_unit_id,
  season_id,
  body
) => {
  return dispatch => {
    const tree_key = newSeasonTreeKey(distribution_id, farming_unit_id);
    return put_season(distribution_id, farming_unit_id, season_id, body).then(
      result => {
        if (result?.season) {
          dispatch({
            type: 'NEW_SEASON_SEASON_SAVED',
            tree_key,
            season: result.season,
          });
        }
        return result?.season;
      },
      err => Promise.reject(v2_error_message(err))
    );
  };
};

// GET v2/distributions into new_season_data.distributions. Resolves with the
// distributions array (or null on failure).
export var load_new_season_distributions = () => {
  return dispatch => {
    dispatch({ type: 'NEW_SEASON_DISTRIBUTIONS_INIT' });
    return get_v2_distributions().then(
      result => {
        const distributions = result?.distributions || [];
        dispatch({ type: 'NEW_SEASON_DISTRIBUTIONS_SUCCESS', distributions });
        return distributions;
      },
      err => {
        dispatch({
          type: 'NEW_SEASON_DISTRIBUTIONS_FAIL',
          error: v2_error_message(err),
        });
        return null;
      }
    );
  };
};

// GET v2/seasons/{d}/{fu}/{season}: resolves with {season, block, field_group}
// (season.sensors included); rejects with a user-facing message.
export var load_new_season_season_detail = (
  distribution_id,
  farming_unit_id,
  season_id
) => {
  return () =>
    get_season(distribution_id, farming_unit_id, season_id).catch(err =>
      Promise.reject(v2_error_message(err))
    );
};
